import { FaStar, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import './ProviderCard.css'

interface ProviderCardProps {
    provider: {
        id: number
        first_name?: string
        last_name?: string
        avatar_url?: string
        profile_picture?: string
        bio?: string
        is_verified?: boolean
        distance?: number
        average_rating?: number
        total_ratings?: number
        total_reviews?: number
        service_location_address?: string
        service_districts?: Array<{ id: number; name?: string }>
        service_offerings?: Array<{
            id: number
            service_name: string
            pricing_type: 'hourly' | 'quote'
            hourly_rate?: number
            base_quote_amount?: number
            requires_quote_visit?: boolean
        }>
    }
}

const ProviderCard = ({ provider }: ProviderCardProps) => {
    const navigate = useNavigate()

    const fullName = `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'Provider'
    const offerings = provider.service_offerings || []
    const rating = provider.average_rating || 0
    const reviews = provider.total_ratings || provider.total_reviews || 0
    const firstHourly = offerings.find((offering) => offering.pricing_type === 'hourly' && offering.hourly_rate)
    
    // Shorten address like mobile app
    const shortenAddress = (address: string) => {
        if (!address) return null
        const parts = address.split(',').map(part => part.trim()).filter(part => part.length > 0)
        if (parts.length <= 2) return address
        const street = parts[0]
        const location = parts[parts.length - 1]
        const result = `${street}, ${location}`
        if (result.length > 40) {
            const truncatedStreet = street.length > 25 ? street.substring(0, 25) + '...' : street
            return `${truncatedStreet}, ${location}`
        }
        return result
    }
    
    const displayAddress = shortenAddress(provider.service_location_address || '')

    const coverageLabel = useMemo(() => {
        if (Array.isArray(provider.service_districts) && provider.service_districts.length > 0) {
            const districtNames = provider.service_districts
                .map((district) => district?.name)
                .filter((name): name is string => Boolean(name))

            if (districtNames.length > 0) {
                if (districtNames.length === 1) return districtNames[0] as string
                if (districtNames.length === 2) return districtNames.join(' • ')

                return `${districtNames.slice(0, 2).join(' • ')} • +${districtNames.length - 2} más`
            }
        }

        return displayAddress || 'Cobertura no disponible'
    }, [displayAddress, provider.service_districts])

    return (
        <div 
            className="provider-card"
            onClick={() => navigate(`/provider/${provider.id}`)}
        >
            <div className="provider-card__header">
                <div className="provider-card__avatar-wrapper">
                    <div className="provider-card__avatar">
                        {provider.avatar_url || provider.profile_picture ? (
                            <img 
                                src={provider.avatar_url || provider.profile_picture} 
                                alt={fullName}
                                className="provider-card__avatar-img"
                            />
                        ) : (
                            <span className="provider-card__avatar-text">
                                {fullName.charAt(0).toUpperCase()}
                            </span>
                        )}
                    </div>
                    {provider.is_verified && (
                        <div className="provider-card__verified-badge">
                            <FaCheckCircle />
                        </div>
                    )}
                </div>
                <div className="provider-card__info">
                    <h3 className="provider-card__name">{fullName}</h3>
                    <div className="provider-card__rating">
                        <FaStar className="provider-card__star" />
                        <span className="provider-card__rating-value">{rating.toFixed(1)}</span>
                        <span className="provider-card__rating-count">({reviews} reseñas)</span>
                    </div>
                </div>
            </div>

            {provider.bio && (
                <p className="provider-card__bio" title={provider.bio}>
                    {provider.bio}
                </p>
            )}

            <div className="provider-card__services-section">
                <span className="provider-card__services-label">Servicios ofrecidos:</span>
                <div className="provider-card__services-list">
                    {offerings.slice(0, 3).map((offering) => (
                        <div key={offering.id} className="provider-card__service-chip">
                            <span>{offering.service_name}</span>
                            <span
                                className={`provider-card__service-pill provider-card__service-pill--${offering.pricing_type}`}
                            >
                                {offering.pricing_type === 'hourly'
                                    ? `S/ ${Number(offering.hourly_rate || 0).toFixed(2)}/h`
                                    : 'Cotización'}
                            </span>
                        </div>
                    ))}
                    {offerings.length > 3 && (
                        <span className="provider-card__more-services">
                            +{offerings.length - 3} más
                        </span>
                    )}
                </div>
            </div>

            <div className="provider-card__footer">
                <div className="provider-card__location">
                    <FaMapMarkerAlt />
                    <span className="provider-card__location-text">
                        {coverageLabel}
                    </span>
                </div>
                {firstHourly && firstHourly.hourly_rate && (
                    <span className="provider-card__price">
                        Desde S/ {Number(firstHourly.hourly_rate).toFixed(2)}/hora
                    </span>
                )}
            </div>
        </div>
    )
}

export default ProviderCard
